import _ from 'lodash';
import { DISPLAY_MODE } from '@/main/app.constants';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import type {
  BaseToolPersistedState,
  DurationWithUnits,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolDisplayModePayload,
  ToolItem,
  ToolParameter,
  ToolParameterDefinition,
  ToolRehydratePayload,
  ToolSetNamePayload,
} from '@/tools/tool.types';

/**
 * Legacy Flux helper used by BaseToolStore handler routing. Zustand tool stores should not use this helper because
 * the caller already knows which store instance it is mutating.
 */
export function shouldHandleToolPayload(toolType: string, payloadType: string): boolean {
  return payloadType === toolType;
}

/**
 * Legacy Flux helper used by BaseToolStore handler routing. Zustand stores should prefer direct per-store actions and
 * invoke reset explicitly from the central display-mode bridge when needed.
 */
export function shouldResetToolStore(toolType: string, payload: ToolDisplayModePayload): boolean {
  return payload.mode === DISPLAY_MODE.NEW && (payload.type === toolType || payload.type === TREND_TOOLS.OVERVIEW);
}

/**
 * Legacy Flux helper used by BaseToolStore handler routing. Zustand tool stores should take the narrowed action
 * arguments directly instead of routing by `payload.type`.
 */
export function shouldSetToolName(
  toolType: string,
  currentId: string | undefined,
  payload: ToolSetNamePayload,
): boolean {
  return payload.type === toolType && (!payload.id || payload.id === currentId);
}

/**
 * Extracts the persisted tool config portion of a rehydrate payload, excluding formula/parameter fields that are
 * re-derived elsewhere during rehydration.
 */
export function extractToolConfigFromPayload(
  payload: ToolRehydratePayload,
  currentAdvancedParametersCollapsed: boolean | undefined,
): ToolConfigParams {
  const rawConfig = _.omit(payload, [
    'type',
    'parameters',
    'formula',
    'signalMetadata',
    'conditionMetadata',
  ]) as Partial<ToolConfigParams>;
  const advancedCollapsed = rawConfig.advancedParametersCollapsed;

  return {
    advancedParametersCollapsed:
      typeof advancedCollapsed === 'boolean' ? advancedCollapsed : Boolean(currentAdvancedParametersCollapsed),
    ..._.omit(rawConfig, ['advancedParametersCollapsed']),
  } as ToolConfigParams;
}

/**
 * Narrows a tool item down to the persisted/displayed properties that should be stored in tool state.
 */
export function sanitizeToolItem(
  item: ToolItem | null | undefined,
  toolItemProps: string[] = DEFAULT_TOOL_ITEM_PROPS,
): ToolItem | null | undefined {
  return _.isNil(item) ? item : (_.pick(item, toolItemProps) as ToolItem);
}

/**
 * Derives the config, original parameter items, and selected parameter-slot values needed to rehydrate a tool store.
 */
export function buildToolRehydrationState<Keys extends string>({
  payload,
  parameterDefinitions = {} as ParameterDefinitions<Keys>,
  currentAdvancedParametersCollapsed = true,
  toolItemProps = DEFAULT_TOOL_ITEM_PROPS,
}: {
  payload: ToolRehydratePayload;
  parameterDefinitions?: ParameterDefinitions<Keys>;
  currentAdvancedParametersCollapsed?: boolean;
  toolItemProps?: string[];
}) {
  const parameters = _.chain(payload.parameters ?? [])
    .reject('unbound')
    .map((parameter) => _.pick(parameter, ['item', 'name']))
    .value();

  const selectedParameters = Object.fromEntries(
    Object.entries<ToolParameterDefinition>(parameterDefinitions).map(([name, definition]) => [
      name,
      selectToolParameterValue(parameters, definition, toolItemProps),
    ]),
  );

  return {
    config: extractToolConfigFromPayload(payload, currentAdvancedParametersCollapsed),
    parameters,
    originalParameters: _.map(parameters, 'item').filter((item): item is ToolItem => item != null),
    selectedParameters,
  };
}

/**
 * Selects the current value for one parameter definition from a list of formula parameters.
 */
export function selectToolParameterValue(
  parameters: ToolParameter[],
  definition: ToolParameterDefinition,
  toolItemProps: string[] = DEFAULT_TOOL_ITEM_PROPS,
): ToolItem | ToolItem[] | null | undefined {
  const iteratee = definition.predicate;
  const selectedItems = _.chain(parameters)
    .filter(iteratee)
    .map((parameter) => sanitizeToolItem(parameter.item, toolItemProps))
    .filter((item): item is ToolItem => item != null)
    .value();

  return definition.multiple ? selectedItems : _.head(selectedItems);
}

/**
 * Reads the shared `BaseToolStore` persisted fields from an arbitrary serialized snapshot.
 */
export function getBasePersistedStateFromSnapshot(
  snapshot: Partial<Record<keyof BaseToolPersistedState, unknown>>,
): BaseToolPersistedState {
  const originalParameters = snapshot.originalParameters as ToolItem[] | undefined;
  const maximumDuration = snapshot.maximumDuration as DurationWithUnits | null | undefined;
  const advancedParametersCollapsed = snapshot.advancedParametersCollapsed as boolean | undefined;
  const configVersion = snapshot.configVersion as string | number | null | undefined;

  return {
    id: snapshot.id as string | undefined,
    name: snapshot.name as string | undefined,
    originalParameters: originalParameters ?? [],
    maximumDuration: maximumDuration ?? undefined,
    advancedParametersCollapsed: advancedParametersCollapsed ?? true,
    configVersion: configVersion ?? undefined,
  };
}

/**
 * Builds the UI config object to persist for a tool, omitting base fields and parameter-slot values that are stored
 * separately.
 */
export function getToolConfigParams({
  serializedState,
  toolType,
  parameterDefinitionKeys,
  advancedParametersCollapsed,
}: {
  serializedState: Record<string, ToolConfigValue>;
  toolType: string;
  parameterDefinitionKeys: string[];
  advancedParametersCollapsed: boolean | undefined;
}): ToolConfigParams {
  const keysToOmit = ['id', 'name', 'originalParameters'].concat(parameterDefinitionKeys);
  const remaining = _.omit(serializedState, keysToOmit) as Record<string, ToolConfigValue>;

  return {
    type: toolType,
    advancedParametersCollapsed: typeof advancedParametersCollapsed === 'boolean' ? advancedParametersCollapsed : true,
    ...remaining,
  };
}

/**
 * Computes the next stored value for a parameter slot after setting one item, including duplicate detection for
 * multi-value parameters.
 */
export function getNextToolParameterValue({
  definition,
  currentValue,
  item,
  toolItemProps = DEFAULT_TOOL_ITEM_PROPS,
}: {
  definition: ToolParameterDefinition;
  currentValue: ToolItem | ToolItem[] | null | undefined;
  item: ToolItem | null | undefined;
  toolItemProps?: string[];
}): { changed: boolean; value: ToolItem | ToolItem[] | null | undefined } {
  const sanitizedItem = sanitizeToolItem(item, toolItemProps);

  if (!definition.multiple) {
    return {
      changed: !_.isEqual(currentValue, sanitizedItem),
      value: sanitizedItem,
    };
  }

  const items = Array.isArray(currentValue) ? currentValue : [];
  if (
    sanitizedItem &&
    _.includes(
      items.map((existingItem) => existingItem.id),
      sanitizedItem.id,
    )
  ) {
    return { changed: false, value: items };
  }

  return {
    changed: true,
    value: sanitizedItem ? items.concat(sanitizedItem) : items,
  };
}

/**
 * Computes the next stored value for a parameter slot after removing one item.
 */
export function getNextToolParameterUnsetValue({
  definition,
  currentValue,
  item,
}: {
  definition: ToolParameterDefinition;
  currentValue: ToolItem | ToolItem[] | null | undefined;
  item: ToolItem | null | undefined;
}): { changed: boolean; value: ToolItem | ToolItem[] | null | undefined } {
  if (!definition.multiple) {
    return {
      changed: currentValue !== undefined,
      value: undefined,
    };
  }

  const existing = Array.isArray(currentValue) ? currentValue : [];
  const nextValue = existing.filter((existingItem) => existingItem.id !== item?.id);

  return {
    changed: nextValue.length !== existing.length,
    value: nextValue,
  };
}

/**
 * Returns a parameter definition or throws when the requested parameter name is not defined for the tool.
 */
export function getRequiredToolParameterDefinition(
  parameterDefinitions: ParameterDefinitions,
  name: string,
): ToolParameterDefinition {
  const definition = parameterDefinitions[name];
  if (!definition) {
    throw new TypeError(`${name} is not a valid parameter item`);
  }

  return definition;
}
